import { useCallback, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { parseShopContext } from '../utils/shopContext';
import { useAppBridgeContext } from '../contexts/AppBridgeContext';
import { redirectToUrl } from '../utils/redirect';

const API_BASE_URL =
  import.meta.env.MODE === 'production'
    ? 'https://ar-space-commerce-production.up.railway.app'
    : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000');

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

interface AuthStatusResponse {
  shop: string;
  authenticated: boolean;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchAuthStatus(url: string, retries = MAX_RETRIES): Promise<{ ok: boolean; data: any }> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url);
      const data = await res.json();
      return { ok: res.ok, data };
    } catch {
      if (attempt < retries) {
        await sleep(RETRY_DELAY_MS);
      }
    }
  }
  throw new Error('auth-unreachable');
}

/**
 * Escape hatch out of the "no context" screen.
 *
 * Reaching this screen means we are loaded top-level with no shop/host, so
 * there is no App Bridge and no in-app navigation that can help: the only way
 * back used to be the browser Back button. We can still rebuild the admin deep
 * link whenever we know which shop this was — sessionStorage keeps shop/host
 * from the last embedded load, and it survives the top-level navigation
 * Shopify performs on a billing return, which is how merchants land here.
 *
 * Rendered only when we can produce a real link; a button that goes nowhere is
 * worse than no button.
 */
function ReturnToAdmin() {
  const { shop } = parseShopContext();
  const clientId = import.meta.env.VITE_SHOPIFY_API_KEY;
  if (!shop || !clientId) return null;

  const handle = shop.replace(/\.myshopify\.com$/i, '');
  const href = `https://admin.shopify.com/store/${handle}/apps/${clientId}`;

  return (
    <a
      href={href}
      style={{
        display: 'inline-block',
        marginTop: 16,
        background: '#5c6ac4',
        color: '#fff',
        borderRadius: 8,
        padding: '10px 24px',
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'none',
      }}
    >
      Open in Shopify Admin
    </a>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { app } = useAppBridgeContext();
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'redirecting' | 'no-context' | 'error'>('loading');

  const checkAuth = useCallback(() => {
    if (location.pathname === '/auth/callback') {
      setStatus('authenticated');
      return;
    }

    const { shop, host } = parseShopContext();
    if (!shop && !host) {
      setStatus('no-context');
      return;
    }

    setStatus('loading');

    const params = new URLSearchParams();
    if (shop) params.set('shop', shop);
    if (host) params.set('host', host);

    fetchAuthStatus(`${API_BASE_URL}/api/auth/status?${params.toString()}`)
      .then(({ ok, data }) => {
        if (!ok && data?.error) {
          setStatus('no-context');
          return;
        }
        const authData = data as AuthStatusResponse;
        if (authData.authenticated) {
          setStatus('authenticated');
          return;
        }
        const authUrl = new URL(`${API_BASE_URL}/api/auth`);
        if (authData.shop || shop) authUrl.searchParams.set('shop', authData.shop || shop!);
        if (host) authUrl.searchParams.set('host', host);
        setStatus('redirecting');
        redirectToUrl(authUrl.toString(), app);
      })
      .catch(() => setStatus('error'));
  }, [location.pathname, app]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (status === 'authenticated') return <>{children}</>;

  const containerStyle: React.CSSProperties = {
    fontFamily: 'system-ui, sans-serif',
    maxWidth: 400,
    margin: '80px auto',
    padding: 24,
    textAlign: 'center',
  };

  if (status === 'no-context') {
    return (
      <div style={containerStyle}>
        <p style={{ color: '#666', lineHeight: 1.6 }}>
          Open this app from Shopify Admin to get started.
        </p>
        <ReturnToAdmin />
      </div>
    );
  }

  if (status === 'redirecting') {
    return (
      <div style={containerStyle}>
        <p style={{ color: '#666' }}>Redirecting to complete installation...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={containerStyle}>
        <p style={{ color: '#c00', marginBottom: 16 }}>
          Unable to verify authentication. Please try again.
        </p>
        <button
          onClick={checkAuth}
          style={{
            background: '#5c6ac4',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
        <div>
          <ReturnToAdmin />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <p style={{ color: '#666' }}>Verifying authentication...</p>
    </div>
  );
}
